import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import { IErrorModel, IPaginatedResponse } from '@core/models/generic.model';
import { ISftpTransfer } from '@core/models/sftp-transfer.model';
import sftpIntegrationService from 'services/api/sftp-integration.service';
import { apiErrorToastMessage } from 'Views/SftpIntegration/sftp-user-messages';

interface ISftpSliceState {
    loading: boolean;
    detailLoading: boolean;
    error: IErrorModel | undefined;
    transfers: IPaginatedResponse<ISftpTransfer> | null;
    selected: ISftpTransfer | null;
}

const initialState: ISftpSliceState = {
    loading: false,
    detailLoading: false,
    error: {} as IErrorModel,
    transfers: null,
    selected: null,
};

export const getSftpTransfersThunk = createAsyncThunk<
    IPaginatedResponse<ISftpTransfer>,
    { page: number; size: number; status?: string; partnerCode?: string; transferDirection?: string },
    { rejectValue: IErrorModel }
>('sftp/getTransfers', async (params, { rejectWithValue }) => {
    try {
        const response: any = await sftpIntegrationService.getTransfers(params);
        return response.data.sftpTransfers;
    } catch (err: any) {
        toast.error(apiErrorToastMessage(err, 'Failed to load SFTP transfers'));
        return rejectWithValue(err);
    }
});

export const getSftpTransferThunk = createAsyncThunk<
    ISftpTransfer,
    number,
    { rejectValue: IErrorModel }
>('sftp/getTransfer', async (id, { rejectWithValue }) => {
    try {
        const response: any = await sftpIntegrationService.getTransfer(id);
        return response.data.sftpTransfer;
    } catch (err: any) {
        toast.error(apiErrorToastMessage(err, 'Failed to load transfer'));
        return rejectWithValue(err);
    }
});

const sftpIntegrationSlice = createSlice({
    name: 'sftpIntegration',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(getSftpTransfersThunk.pending, (state) => {
                state.loading = true;
            })
            .addCase(getSftpTransfersThunk.fulfilled, (state, action) => {
                state.loading = false;
                state.transfers = action.payload;
            })
            .addCase(getSftpTransfersThunk.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            .addCase(getSftpTransferThunk.pending, (state) => {
                state.detailLoading = true;
                state.selected = null;
            })
            .addCase(getSftpTransferThunk.fulfilled, (state, action) => {
                state.detailLoading = false;
                state.selected = action.payload;
            })
            .addCase(getSftpTransferThunk.rejected, (state) => {
                state.detailLoading = false;
            });
    },
});

export default sftpIntegrationSlice;
export const sftpIntegrationReducer = sftpIntegrationSlice.reducer;
